
import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import CloseIcon from "../Icons/CloseIcon";
import { childTreeValidator } from "../../utils/Components";

const childValidator = (child) => {
  if (!child || !child.props) {
     return true;
  }
  return child.type !== ErgoModal; // Prevent nested Modal
};

const propTypes = {
  open: PropTypes.bool.isRequired,
  dismissible: PropTypes.bool,
  onDismiss: PropTypes.func,
  backdropDismissesModal: PropTypes.bool,
  dismissTimeout: PropTypes.number,
  useEscKeyToDismiss: PropTypes.bool,
  viewport: PropTypes.oneOf(["fixed", "relative"]),
  children: childTreeValidator(
    childValidator,
    (componentName) =>
      `${componentName} cannot contain another ${componentName} instance as child element.`
  ),
  className: PropTypes.string,
  id: PropTypes.string,
};

const ErgoModal = ({
  className,
  children,
  id,
  open = false,
  dismissible = false,
  backdropDismissesModal = false,
  dismissTimeout,
  onDismiss,
  useEscKeyToDismiss = true,
  viewport = "fixed",
}) => {
  const backdropRef = useRef(null);
  const timeoutHandler = useRef(null);

  // ✅ Proper useCallback: the callback itself is memoized
  const dismiss = useCallback(() => {
    if (typeof onDismiss === "function") {
      onDismiss();
    }
  }, [onDismiss]);

  const handleBackdrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (dismissible && backdropDismissesModal) {
        dismiss();
      }
    },
    [dismissible, backdropDismissesModal, dismiss]
  );

  const handleKeyDown = useCallback(
    (event) => {
      // Escape key
      if (dismissible && useEscKeyToDismiss && event.keyCode === 27) {
        event.stopPropagation();
        event.preventDefault();
        dismiss();
      }
    },
    [dismissible, useEscKeyToDismiss, dismiss]
  );

  const contentClickHandler = useCallback((event) => {
    event.stopPropagation(); // Prevent backdrop click
  }, []);

  const clearDismissTimeout = useCallback(() => {
    if (timeoutHandler.current) {
      clearTimeout(timeoutHandler.current);
      timeoutHandler.current = null;
    }
  }, []);

  const setDismissTimeout = useCallback(() => {
    const timeout = typeof dismissTimeout === "number" ? dismissTimeout : 0;
    // Clear any existing timeout before setting a new one
    clearDismissTimeout();
    timeoutHandler.current = setTimeout(() => {
      dismiss();
      clearDismissTimeout();
    }, timeout);
  }, [dismissTimeout, dismiss, clearDismissTimeout]);

  useEffect(() => {
    if (open && (dismissTimeout ?? 0) > 0) {
      setDismissTimeout();
    }
    return clearDismissTimeout;
    // Depend on the helpers to ensure correct cleanup and re-scheduling
  }, [open, dismissTimeout, setDismissTimeout, clearDismissTimeout]);

  useEffect(() => {
    if (open && backdropRef.current) {
      backdropRef.current.focus();
    }
  }, [open]);

  const componentClassName = classNames(className, "ee_modal", {
    "ee_modal--relative": viewport === "relative",
    "ee_modal--hidden": !open,
  });

  const iconDescription = "Schließen";

  return (
    <div
      ref={backdropRef}
      className={componentClassName}
      onClick={handleBackdrop}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      id={id}
    >
      <div className="ee_modal__main" onClick={contentClickHandler}>
        {dismissible && (
          <div className="ee_modal__header">
            <button
              type="button"
              className="ee_modal__close-button"
              onClick={dismiss}
              aria-label={iconDescription}
            >
              <CloseIcon
                title={iconDescription}
                className="ee_modal__close-icon"
                width={22}
                height={22}
              />
            </button>
          </div>
        )}
        <section className="ee_modal__content">{children}</section>
      </div>
    </div>
  );
};

ErgoModal.propTypes = propTypes;

export default ErgoModal;

